/**
 * Delivery Estimation — GoPadFree
 * Adapted from delivery-estimation-theme-ext/assets/ExpDelivery.js
 *
 * The delivery sentences (dates, "not deliverable", "invalid pincode") come
 * from the web server and are edited on its ShopifyApp → Delivery Estimate
 * page. The shop goes in X-Shopify-Shop-Domain so the server uses this store's
 * settings. Only the two texts shown while the server is busy or unreachable
 * live here, as theme editor settings.
 *
 * The order cut-off time, delivery days, off days and holidays are also set on
 * that page (worked out in Indian time on the server). The theme's old
 * "Pickup cut-off time" setting was never used and has been removed.
 */
class DeliveryEstimator {
  constructor(root) {
    this.apiEndpoint = 'https://www.webserver.healthfab.in/calculateDeliveryTime';
    this.root       = root;
    this.input      = root.querySelector('.dest-input');
    this.btn        = root.querySelector('.dest-btn');
    this.output     = root.querySelector('.dest-output');
    this.shop       = root.dataset.shop || '';
    this.checkingText    = root.dataset.checkingText || 'Checking delivery availability…';
    this.unavailableText = root.dataset.unavailableText || 'Unable to check delivery. Please try again.';

    if (this.btn) this.btn.addEventListener('click', () => this.handleSubmit());
    if (this.input) {
      this.input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') this.handleSubmit();
      });
      // Digits only
      this.input.addEventListener('input', () => {
        this.input.value = this.input.value.replace(/[^0-9]/g, '');
      });
    }
  }

  async handleSubmit() {
    const pincode = this.input ? this.input.value.trim() : '';

    // A wrong pincode is still sent: the server answers 400 with its own
    // (editable) "invalid pincode" sentence.
    this.show(this.checkingText, 'loading');
    this.btn.disabled = true;

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (this.shop) headers['X-Shopify-Shop-Domain'] = this.shop;
      const res = await fetch(this.apiEndpoint, {
        method:  'POST',
        headers,
        body:    JSON.stringify({ postal_code: pincode, payment_method: this.getPaymentMethod() }),
      });

      if (!res.ok && res.status !== 400) throw new Error('Network error ' + res.status);

      const data = await res.json();
      this.display(data);
    } catch (err) {
      console.error('[DeliveryEstimator]', err);
      this.show(this.unavailableText, 'error');
    } finally {
      this.btn.disabled = false;
    }
  }

  getPaymentMethod() {
    return document.querySelector('input[name="payment_method"]:checked')?.value || 'cod';
  }

  // success / fallback: a delivery date (green). not_serviceable / error: the
  // server's sentence (red).
  display(data) {
    if (data.status === 'success' || data.status === 'fallback') {
      this.show(data.delivery_message || data.message, 'success');
    } else {
      this.show(data.message || 'Delivery information not available for this pincode.', 'error');
    }
  }

  show(msg, type = 'info') {
    if (!this.output) return;
    // Text, never HTML: the sentences are edited on the web server.
    this.output.textContent = msg || '';
    // Keep the block's base class (size, spacing) and use the block's
    // --success / --error / --loading colours; dest-output is how the script
    // finds the box.
    this.output.className = 'dest-block__output dest-output dest-block__output--' + type;
  }
}

// Init every instance on the page (supports multiple sections / quick-view)
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.dest-widget').forEach((el) => new DeliveryEstimator(el));
});
